<template>
	<div>
		<h1 class="heading-2 line-height-150 mt-50 mb-100-smo mb-200-md"> Reset password </h1>
		<form @submit.prevent>
			<p> To reset your password online, let&rsquo;s start by entering your&nbsp;email: </p>
			<InputField class="mt-100 mb-200" :field="v$.email" field-name="Email Address">
				<template #label>Email address</template>
				<template #input>
					<input
						v-model="v$.email.$model"
						data-test="email"
						name="email"
						class="input"
						type="email"
						maxlength="150"
						@clear-server="serverValidationHandler.clearServerErrors('email')"
					/>
				</template>
			</InputField>
			<BaseButtonGroup>
				<template #secondary>
					<BaseLink class="button button-outline" :link="{ href: '/login' }"> Go back </BaseLink>
				</template>
				<template #primary>
					<SubmitButton
						data-test="forgot-password-form-submit"
						:is-working="isSubmitted"
						:v$="v$"
						@sb-click="submit"
					>
						Continue
					</SubmitButton>
				</template>
			</BaseButtonGroup>
		</form>
	</div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { email, maxLength, required } from '@vuelidate/validators';
import { ForgotPasswordEmailSubmission } from 'types/forgot-password';
import { forgotPasswordErrorRoute } from '@utils/forgot-password-error-routing';
import { getDistinctId } from '@utils/analytics';
import InputField from '@components/form/input-field.vue';
import { resetPasswordEmailRequest } from '@api/forgot-password';
import ServerValidationHandler from '@classes/ServerValidationHandler';
import SubmitButton from '@components/form/submit-button.vue';
import { useHead } from '@unhead/vue';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
const router = useRouter();
const serverValidationHandler = ref(new ServerValidationHandler());

interface ComponentState {
	email: string;
}
const state: ComponentState = reactive({
	email: ''
});
const rules = computed(() => {
	return {
		email: {
			required,
			email,
			maxLength: maxLength(150),
			serverError: serverValidationHandler.value.serverError('email')
		}
	};
});
const v$ = useVuelidate(rules, state);

const isSubmitted = ref(false);

async function submit(): Promise<void> {
	isSubmitted.value = true;

	const submissionForm: ForgotPasswordEmailSubmission = {
		username: state.email,
		mixpanelId: getDistinctId() || ''
	};

	try {
		await resetPasswordEmailRequest(submissionForm);

		await router.push({ name: 'forgot-password-email-sent' }).catch(() => {});

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		serverValidationHandler.value.handleServerValidation(error);

		if (error.response?.data?.errorCode) {
			const errorRoute = forgotPasswordErrorRoute(error.response.data.errorCode);
			try {
				await router.push({
					name: errorRoute
				});
			} catch {}
		}

		isSubmitted.value = false;
	} finally {
		isSubmitted.value = false;
	}
}

function setup(): void {
	useHead({
		title: 'Reset password'
	});
}
setup();
</script>
